import { inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { User } from '@klubovna/shared-models';
import { AuthService, NotificationService } from '../../services';
import { ConsoleRoute, homeRouteForRole } from '../../utils/home-route.util';
import { catchError, exhaustMap, filter, map, mergeMap, of, switchMap, tap, withLatestFrom } from 'rxjs';
import * as UserActions from './user.actions';
import { selectIsAuthenticated } from './user.selectors';

@Injectable()
export class UserEffects {
  private actions$ = inject(Actions);
  private store = inject(Store);
  private authService = inject(AuthService);
  private router = inject(Router);
  private notificationService = inject(NotificationService);
  private translate = inject(TranslateService);

  login$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserActions.login),
      mergeMap(({ credentials }) =>
        this.authService.login(credentials).pipe(
          map((response) => {
            if (!response || !response.user) {
              throw new Error('Invalid login response');
            }
            return UserActions.loginSuccess({ response });
          }),
          catchError((error) => {
            let errorMessage = 'Login failed';

            if (error.error?.message) {
              if (Array.isArray(error.error.message)) {
                errorMessage = error.error.message[0];
              } else {
                errorMessage = error.error.message;
              }
            } else if (error.message) {
              errorMessage = error.message;
            }

            this.notificationService.errorRaw(errorMessage, this.translate.instant('auth.login.error'));
            return of(UserActions.loginFailure({ error: errorMessage }));
          }),
        ),
      ),
    ),
  );

  loginSuccess$ = createEffect(() =>
      this.actions$.pipe(
        ofType(UserActions.loginSuccess),
        tap(({ response }) => {
          this.authService.setAuthData(response);
          if (!response.user) {
            this.notificationService.errorRaw(
              this.translate.instant('auth.login.error'),
              this.translate.instant('auth.login.error'),
            );
            return;
          }
          if (response.user.mustChangePassword) {
            this.notificationService.infoRaw(
              this.translate.instant('auth.change_password.forced_message'),
              this.translate.instant('auth.change_password.forced_title'),
            );
            this.router.navigate([ ConsoleRoute.ChangePassword ]);
            return;
          }
          this.notificationService.successRaw(
            `${ this.translate.instant('auth.login.success') } ${ response.user.firstName }!`,
            this.translate.instant('auth.login.success'),
          );
          const currentUrl = this.router.url;
          if (currentUrl.startsWith('/login') && currentUrl.includes('returnUrl=')) {
            return;
          }
          if (currentUrl.startsWith('/admin') || currentUrl.startsWith('/volunteer') || currentUrl.startsWith('/pos')) {
            return;
          }
          this.redirectBasedOnRole(response.user);
        }),
      ),
    { dispatch: false },
  );

  logout$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserActions.logout),
      tap(() => {
        this.authService.clearAuthData();
      }),
      map(() => UserActions.logoutSuccess()),
    ),
  );

  logoutSuccess$ = createEffect(() =>
      this.actions$.pipe(
        ofType(UserActions.logoutSuccess),
        tap(() => {
          const currentUrl = this.router.url;
          const shouldRedirectToHome = currentUrl.startsWith('/admin') || currentUrl.startsWith('/volunteer') || currentUrl.startsWith('/pos');
          if (shouldRedirectToHome) {
            this.router.navigate([ '/' ]);
          }
        }),
      ),
    { dispatch: false },
  );

  initializeAuth$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserActions.initializeAuth),
      mergeMap(() => {
        const hasToken = this.authService.hasStoredToken();

        if (hasToken) {
          return of(UserActions.validateToken());
        } else {
          return of(UserActions.initializeAuthFailure());
        }
      }),
    ),
  );

  validateToken$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserActions.validateToken),
      withLatestFrom(this.store.select(selectIsAuthenticated)),
      filter(([ , isAuthenticated ]) => !isAuthenticated),
      exhaustMap(() => {
        const tokenBeforeValidation = this.authService.getToken();
        return this.authService.validateToken().pipe(
          map((user) => {
            if (user) {
              return UserActions.validateTokenSuccess({ user });
            } else {
              if (this.authService.getToken() === tokenBeforeValidation) {
                this.authService.clearAuthData();
              }
              return UserActions.validateTokenFailure();
            }
          }),
          catchError(() => {
            if (this.authService.getToken() === tokenBeforeValidation) {
              this.authService.clearAuthData();
            }
            return of(UserActions.validateTokenFailure());
          }),
        );
      }),
    ),
  );

  validateTokenSuccess$ = createEffect(() =>
      this.actions$.pipe(
        ofType(UserActions.validateTokenSuccess),
        tap(({ user }) => {
          this.authService.updateUserInStorage(user);
          if (user.mustChangePassword) {
            this.router.navigate([ ConsoleRoute.ChangePassword ]);
          }
        }),
      ),
    { dispatch: false },
  );

  validateTokenFailure$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserActions.validateTokenFailure),
      withLatestFrom(this.store.select(selectIsAuthenticated)),
      filter(([ , isAuthenticated ]) => !isAuthenticated),
      map(() => UserActions.forceLogout()),
    ),
  );

  initializeAuthFailure$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserActions.initializeAuthFailure),
      map(() => UserActions.forceLogout()),
    ),
  );

  updateProfile$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserActions.updateProfile),
      switchMap(({ data }) =>
        this.authService.updateProfile(data).pipe(
          map((user) => UserActions.updateProfileSuccess({ user })),
          catchError((error) => {
            const message = this.getErrorMessage(error, 'volunteer.profile.updateError');
            this.notificationService.error('volunteer.profile.updateError');
            return of(UserActions.updateProfileFailure({ error: message }));
          }),
        ),
      ),
    ),
  );

  updateProfileSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(UserActions.updateProfileSuccess),
        tap(({ user }) => {
          this.authService.updateUserInStorage(user);
          this.notificationService.success('volunteer.profile.updateSuccess');
        }),
      ),
    { dispatch: false },
  );

  uploadProfilePhoto$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserActions.uploadProfilePhoto),
      switchMap(({ file }) =>
        this.authService.uploadProfilePhoto(file).pipe(
          map((user) => UserActions.uploadProfilePhotoSuccess({ user })),
          catchError((error) => {
            const message = this.getErrorMessage(error, 'volunteer.profile.photoUploadError');
            this.notificationService.error('volunteer.profile.photoUploadError');
            return of(UserActions.uploadProfilePhotoFailure({ error: message }));
          }),
        ),
      ),
    ),
  );

  uploadProfilePhotoSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(UserActions.uploadProfilePhotoSuccess),
        tap(({ user }) => {
          this.authService.updateUserInStorage(user);
          this.notificationService.success('volunteer.profile.photoUploadSuccess');
        }),
      ),
    { dispatch: false },
  );

  deleteProfilePhoto$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserActions.deleteProfilePhoto),
      switchMap(() =>
        this.authService.deleteProfilePhoto().pipe(
          map((user) => UserActions.deleteProfilePhotoSuccess({ user })),
          catchError((error) => {
            const message = this.getErrorMessage(error, 'volunteer.profile.photoDeleteError');
            this.notificationService.error('volunteer.profile.photoDeleteError');
            return of(UserActions.deleteProfilePhotoFailure({ error: message }));
          }),
        ),
      ),
    ),
  );

  deleteProfilePhotoSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(UserActions.deleteProfilePhotoSuccess),
        tap(({ user }) => {
          this.authService.updateUserInStorage(user);
          this.notificationService.success('volunteer.profile.photoDeleteSuccess');
        }),
      ),
    { dispatch: false },
  );

  private redirectBasedOnRole(user: User): void {
    this.router.navigate([ homeRouteForRole(user.role, user.additionalRoles) ]);
  }

  private getErrorMessage(error: any, fallbackKey: string): string {
    if (error?.error?.message) {
      return Array.isArray(error.error.message) ? error.error.message[0] : error.error.message;
    }
    if (error?.message) {
      return error.message;
    }
    return this.translate.instant(fallbackKey);
  }
}
